import { Button, Crumbs, Notice, PageHead, Panel, PanelHead, Pill } from '@concord/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import type { Me } from '../../lib/session';
import { ConsoleLayout } from '../console/ConsoleLayout';
import { POOLS_KEY, PoolEditor, type PoolSummary } from './PoolEditor';
import s from './pools.module.css';

/** Physician pools by cancer type (not in the prototype; a free Phase 1 addition). */
export function PoolsPage({ me }: { me: Me }) {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const canManage = me.permissions.includes('accounts.manage');
  const q = useQuery({
    queryKey: POOLS_KEY,
    queryFn: () => api<{ items: PoolSummary[] }>('/staff/pools'),
  });
  const active = q.data?.items.filter((p) => p.active) ?? [];
  const retired = q.data?.items.filter((p) => !p.active) ?? [];

  const table = (rows: PoolSummary[]) => (
    <div className="tw">
      <table className={s.table}>
        <thead>
          <tr>
            <th>Pool</th>
            <th>Cancer types</th>
            <th>Specialty</th>
            <th className={s.num}>Physicians</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id}>
              <td>
                <Link to={`/console/pools/${p.id}`}>{p.name}</Link>
                {p.description && <div className="sub2">{p.description}</div>}
              </td>
              <td>{p.caseTypes.length ? p.caseTypes.map((t) => t.term).join(', ') : 'Any'}</td>
              <td>{p.specialty?.term ?? 'Any'}</td>
              <td className={s.num}>{p.members}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <ConsoleLayout me={me} current="pools">
      <Crumbs items={[{ label: 'Physician pools' }]} />
      <PageHead
        title="Physician pools"
        when="Groups of physicians by specialty and cancer type, not tied to any plan. Choose a pool when building a panel."
        actions={canManage && <Button onClick={() => setCreating(true)}>New pool</Button>}
      />
      {q.isError && <Notice tone="bad">{errorMessage(q.error)}</Notice>}
      {q.data && (
        <Panel aria-label="Pools" style={{ marginBottom: '1.25rem' }}>
          <PanelHead title="Pools" sub={`${active.length} ${active.length === 1 ? 'pool' : 'pools'}`} />
          {active.length ? (
            table(active)
          ) : (
            <p className={s.empty}>
              No pools yet.{canManage && ' Create one to group physicians by the cancers they treat.'}
            </p>
          )}
        </Panel>
      )}
      {retired.length > 0 && (
        <Panel aria-label="Retired pools">
          <PanelHead title="Retired" sub="Not offered on Build the panel">
            <Pill tone="idle">{retired.length}</Pill>
          </PanelHead>
          {table(retired)}
        </Panel>
      )}
      <p className={s.note}>
        A pool is separate from a plan's network: the network decides whether a physician is in plan, and an
        out-of-plan choice is still recorded on the case.
      </p>
      <PoolEditor
        open={creating}
        onOpenChange={setCreating}
        onSaved={(id) => navigate(`/console/pools/${id}`)}
      />
    </ConsoleLayout>
  );
}
